import Keycloak from "keycloak-js";
import getEnv from "Utils/getEnv";

const TOKEN_KEY = "kc_token";
const REFRESH_TOKEN_KEY = "kc_refresh_token";
const ID_TOKEN_KEY = "kc_id_token";
const KEYCLOAK_CALLBACK_PARAMS = [
  "code",
  "session_state",
  "state",
  "iss",
  "kc_action_status",
  "error",
  "error_description",
  "error_uri",
];
const DEFAULT_KEYCLOAK_CONFIG = {
  url: "https://auth.dev.sandbox.kewordal.com",
  realm: "dev",
  clientId: "public-web",
};
let keycloakInitPromise = null;

const keycloakConfig = {
  url: getEnv("KEYCLOAK_URL", DEFAULT_KEYCLOAK_CONFIG.url),
  realm: getEnv("KEYCLOAK_REALM", DEFAULT_KEYCLOAK_CONFIG.realm),
  clientId: getEnv("KEYCLOAK_CLIENT_ID", DEFAULT_KEYCLOAK_CONFIG.clientId),
};
const keycloakUrl = getEnv("KEYCLOAK_EXTERNAL_URL", keycloakConfig.url);

const keycloak = new Keycloak(keycloakConfig);

const normalizeUrl = (value) => String(value || "").replace(/\/+$/, "");

const rewriteKeycloakRedirectUrl = (url) => {
  const externalUrl = normalizeUrl(keycloakUrl);
  const internalUrl = normalizeUrl(keycloakConfig.url);

  if (!url || !externalUrl || externalUrl === internalUrl) {
    return url;
  }

  if (url.startsWith(internalUrl)) {
    return `${externalUrl}${url.slice(internalUrl.length)}`;
  }

  if (typeof window === "undefined") {
    return url;
  }

  const appOrigin = window.location.origin;
  const internalAbsoluteUrl = normalizeUrl(new URL(internalUrl, appOrigin).toString());

  if (url.startsWith(internalAbsoluteUrl)) {
    return `${externalUrl}${url.slice(internalAbsoluteUrl.length)}`;
  }

  return url;
};

const rewriteKeycloakUrlFactory = (methodName) => {
  const originalMethod = keycloak[methodName]?.bind(keycloak);
  if (!originalMethod) return;

  keycloak[methodName] = (...args) => {
    const url = originalMethod(...args);
    if (url && typeof url.then === "function") {
      return url.then(rewriteKeycloakRedirectUrl);
    }
    return rewriteKeycloakRedirectUrl(url);
  };
};

[
  "createLoginUrl",
  "createLogoutUrl",
  "createRegisterUrl",
  "createAccountUrl",
].forEach(rewriteKeycloakUrlFactory);

export default keycloak;

export function createKeycloakAuthUrl({
  redirectUri,
  action,
  scope = "openid",
}) {
  const baseUrl =
    action === "register"
      ? `${normalizeUrl(keycloakUrl)}/realms/${keycloakConfig.realm}/protocol/openid-connect/registrations`
      : `${normalizeUrl(keycloakUrl)}/realms/${keycloakConfig.realm}/protocol/openid-connect/auth`;

  const params = new URLSearchParams({
    client_id: keycloakConfig.clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope,
  });

  if (action && action !== "register") {
    params.set("kc_action", action);
  }

  return `${baseUrl}?${params.toString()}`;
}

export function createKeycloakLoginUrl(redirectUri) {
  return createKeycloakAuthUrl({ redirectUri });
}

export function createKeycloakRegisterUrl(redirectUri) {
  return createKeycloakAuthUrl({ redirectUri, action: "register" });
}

export function getUrlWithoutKeycloakCallbackParams(href) {
  if (!href) return href;

  const url = new URL(href, window.location.origin);
  let modified = false;

  KEYCLOAK_CALLBACK_PARAMS.forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      modified = true;
    }
  });

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    KEYCLOAK_CALLBACK_PARAMS.forEach((param) => {
      if (hashParams.has(param)) {
        hashParams.delete(param);
        modified = true;
      }
    });
    const nextHash = hashParams.toString();
    url.hash = nextHash ? `#${nextHash}` : "";
  }

  return modified ? url.toString() : href;
}

export function clearKeycloakCallbackParamsFromUrl({
  onlyIfMissingState = false,
} = {}) {
  if (typeof window === "undefined") return;

  const href = window.location.href;
  const url = new URL(href);
  const hasCallbackParam = KEYCLOAK_CALLBACK_PARAMS.some(
    (param) => url.searchParams.has(param) || url.hash.includes(`${param}=`),
  );
  const hasState =
    url.searchParams.has("state") || new URLSearchParams(url.hash.replace(/^#/, "")).has("state");

  if (!hasCallbackParam || (onlyIfMissingState && hasState)) {
    return;
  }

  const cleanUrl = getUrlWithoutKeycloakCallbackParams(href);
  window.history.replaceState({}, document.title, cleanUrl);
}

export function getCleanKeycloakRedirectUri(href) {
  if (typeof window === "undefined") return href;
  return getUrlWithoutKeycloakCallbackParams(href || window.location.href);
}

function buildKeycloakInitOptions(options = {}) {
  const initOptions = {
    pkceMethod: "S256",
    enableLogging: false,
    checkLoginIframe: false,
    silentCheckSsoFallback: false,
    ...options,
  };

  if (typeof window !== "undefined") {
    initOptions.redirectUri =
      initOptions.redirectUri || getCleanKeycloakRedirectUri(window.location.href);
  }

  return initOptions;
}

function saveTokens() {
  if (keycloak.token) localStorage.setItem(TOKEN_KEY, keycloak.token);
  if (keycloak.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, keycloak.refreshToken);
  if (keycloak.idToken) localStorage.setItem(ID_TOKEN_KEY, keycloak.idToken);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
}

export function clearStoredKeycloakTokens() {
  clearTokens();
}

export function saveStoredKeycloakTokens() {
  saveTokens();
}

export function initKeycloak(options = {}) {
  if (keycloakInitPromise) {
    return keycloakInitPromise;
  }

  if (keycloak.didInitialize) {
    return Promise.resolve(Boolean(keycloak.authenticated));
  }

  const storedToken = localStorage.getItem(TOKEN_KEY);
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const storedIdToken = localStorage.getItem(ID_TOKEN_KEY);
  const initOptions = buildKeycloakInitOptions(options);

  if (storedToken && storedRefreshToken) {
    keycloak.token = storedToken;
    keycloak.refreshToken = storedRefreshToken;
    keycloak.idToken = storedIdToken || undefined;

    keycloakInitPromise = keycloak.init({
      token: storedToken,
      refreshToken: storedRefreshToken,
      idToken: storedIdToken || undefined,
      onLoad: initOptions.onLoad || "none",
      ...initOptions,
    }).then((authenticated) => {
      if (authenticated) {
        saveTokens();
        return true;
      }
      clearTokens();
      keycloak.token = null;
      keycloak.refreshToken = null;
      keycloak.idToken = null;
      return false;
    }).catch(() => {
      clearTokens();
      keycloak.token = null;
      keycloak.refreshToken = null;
      keycloak.idToken = null;
      return false;
    });

    return keycloakInitPromise;
  }

  keycloakInitPromise = keycloak.init({
    onLoad: initOptions.onLoad || "none",
    ...initOptions,
  }).then((authenticated) => {
    if (authenticated) saveTokens();
    return authenticated;
  });

  return keycloakInitPromise;
}

export async function redirectToKeycloakLogin(redirectUri) {
  try {
    await initKeycloak();
  } catch {}
  keycloak.login({ redirectUri });
}
